const CX = 320;
const CY = 310;
const ISO = 0.866;

export const P = (x, y, z = 0) => [CX + (x - y) * ISO, CY - z + (x + y) * 0.5];
export const pts = (...ps) => ps.map((p) => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");

export const Box = ({ x, y, w, d, z0, h, t, l, r, stroke }) => {
    const a = w / 2;
    const b = d / 2;
    const pT = P(x - a, y - b, z0 + h);
    const pR = P(x + a, y - b, z0 + h);
    const pF = P(x + a, y + b, z0 + h);
    const pL = P(x - a, y + b, z0 + h);
    const drop = (p) => [p[0], p[1] + h];
    return (
        <g>
            <polygon points={pts(pL, pF, drop(pF), drop(pL))} fill={l} />
            <polygon points={pts(pF, pR, drop(pR), drop(pF))} fill={r} />
            <polygon
                points={pts(pT, pR, pF, pL)}
                fill={t}
                stroke={stroke || "none"}
                strokeWidth={stroke ? 1 : 0}
            />
        </g>
    );
};

export const Shadow = ({ x, y, rx, ry, o = 0.05 }) => {
    const [cx, cy] = P(x, y, 20.4);
    return <ellipse cx={cx} cy={cy} rx={rx} ry={ry} fill="#16324F" opacity={o} />;
};

export const Slab = () => (
    <g>
        <Box x={0} y={0} w={380} d={300} z0={0} h={20} t="#F5F6FA" l="#E6E9F1" r="#DBDFEA" stroke="#E2E6F0" />
        {[-95, 0, 95].map((t) => (
            <g key={t} stroke="#D9DEE9" strokeOpacity={0.55} strokeWidth={1}>
                <line x1={P(-180, t, 20.3)[0]} y1={P(-180, t, 20.3)[1]} x2={P(180, t, 20.3)[0]} y2={P(180, t, 20.3)[1]} />
                <line x1={P(t, -140, 20.3)[0]} y1={P(t, -140, 20.3)[1]} x2={P(t, 140, 20.3)[0]} y2={P(t, 140, 20.3)[1]} />
            </g>
        ))}
    </g>
);

export const Chair = ({ x, y, facing = 1 }) => {
    const [bx, by] = P(x, y, 21);
    const stemB = P(x, y, 24);
    const stemT = P(x, y, 40);
    return (
        <g>
            <ellipse cx={bx} cy={by} rx={12} ry={5} fill="#16324F" opacity={0.1} />
            <ellipse cx={bx} cy={by} rx={10.5} ry={4.5} fill="#46536F" />
            <line x1={stemB[0]} y1={stemB[1]} x2={stemT[0]} y2={stemT[1]} stroke="#5A688A" strokeWidth={2.6} />
            <Box x={x} y={y} w={21} d={21} z0={40} h={5} t="#66769A" l="#55648A" r="#48557A" />
            <Box x={x} y={y - 10 * facing} w={20} d={4.5} z0={45} h={29} t="#66769A" l="#55648A" r="#48557A" />
        </g>
    );
};

export const Desk = ({ x, y, w = 170, divider = true }) => {
    const [cx, cy] = P(x, y, 20.4);
    return (
        <g>
            <ellipse cx={cx} cy={cy} rx={w / 2 + 12} ry={(w / 2 + 12) * 0.34} fill="#16324F" opacity={0.05} />
            {[-1, 1].map((sx) =>
                [-1, 1].map((sy) => (
                    <Box key={`${sx}${sy}`} x={x + sx * (w / 2 - 7)} y={y + sy * 10} w={5} d={5} z0={20} h={32} t="#AEB7CB" l="#98A2BA" r="#8791AC" />
                )),
            )}
            <Box x={x} y={y} w={w} d={30} z0={52} h={6} t="#FBFCFE" l="#E9ECF3" r="#DDE1EC" stroke="#D5DAE6" />
            {divider && (
                <Box x={x} y={y} w={w - 40} d={3} z0={58} h={14} t="rgba(206,221,245,0.55)" l="rgba(190,208,238,0.4)" r="rgba(180,198,230,0.35)" />
            )}
        </g>
    );
};

export const Laptop = ({ x, y, z, flip = false }) => {
    const s = flip ? 1 : -1;
    const b1 = P(x - 13, y + 8 * s, z + 2.5);
    const b2 = P(x + 13, y + 8 * s, z + 2.5);
    const t1 = P(x - 12, y + 11 * s, z + 22);
    const t2 = P(x + 12, y + 11 * s, z + 22);
    return (
        <g>
            <Box x={x} y={y} w={26} d={17} z0={z} h={2.5} t="#D3D9E6" l="#B4BDD0" r="#A4AEC4" />
            <polygon points={pts(b1, b2, t2, t1)} fill="url(#wsScreen)" opacity={0.9} />
        </g>
    );
};

export const PersonSeated = ({ x, y, color = "#33415C" }) => {
    const [bx, by] = P(x, y, 63);
    const [hx, hy] = P(x, y, 88);
    return (
        <g fill={color} opacity={0.92}>
            <rect x={bx - 9} y={by - 15} width={18} height={32} rx={9} />
            <circle cx={hx} cy={hy} r={7.5} />
        </g>
    );
};

export const PersonStanding = ({ x, y, color = "#16324F" }) => {
    const [bx, by] = P(x, y, 52);
    const [hx, hy] = P(x, y, 96);
    return (
        <g fill={color} opacity={0.9}>
            <ellipse cx={P(x, y, 20.5)[0]} cy={P(x, y, 20.5)[1]} rx={11} ry={4.5} fill="#16324F" opacity={0.14} />
            <rect x={bx - 10} y={by - 32} width={20} height={64} rx={10} />
            <circle cx={hx} cy={hy} r={8.5} />
        </g>
    );
};

export const Plant = ({ x, y, s = 1, z = 20 }) => {
    const [cx, cy] = P(x, y, z + 34);
    return (
        <g>
            <ellipse cx={P(x, y, z + 0.5)[0]} cy={P(x, y, z + 0.5)[1]} rx={13 * s} ry={5 * s} fill="#16324F" opacity={0.08} />
            <Box x={x} y={y} w={17 * s} d={17 * s} z0={z} h={15} t="#D99B74" l="#C98B6A" r="#B57A58" />
            <ellipse cx={cx} cy={cy} rx={6 * s} ry={17 * s} fill="#3FA98C" transform={`rotate(-28 ${cx} ${cy})`} />
            <ellipse cx={cx} cy={cy} rx={6 * s} ry={20 * s} fill="#63BE9C" />
            <ellipse cx={cx} cy={cy} rx={6 * s} ry={17 * s} fill="#8FD3AE" transform={`rotate(28 ${cx} ${cy})`} />
        </g>
    );
};

export const Cup = ({ x, y, z }) => {
    const [cx, cy] = P(x, y, z);
    return (
        <g>
            <rect x={cx - 3.5} y={cy - 8} width={7} height={8} rx={1.4} fill="#F4F6FA" stroke="#D8DEE9" strokeWidth={0.8} />
            <ellipse cx={cx} cy={cy - 8} rx={3.5} ry={1.6} fill="#FFFFFF" stroke="#D8DEE9" strokeWidth={0.8} />
        </g>
    );
};

export const Lamp = ({ x, y, z }) => {
    const b = P(x, y, z);
    const t = P(x, y, z + 22);
    return (
        <g>
            <line x1={b[0]} y1={b[1]} x2={t[0]} y2={t[1]} stroke="#8A94AC" strokeWidth={2} />
            <circle cx={t[0]} cy={t[1]} r={4} fill="#16324F" />
            <ellipse cx={t[0]} cy={t[1] + 15} rx={11} ry={4.5} fill="url(#wsGlow)" opacity={0.45} />
        </g>
    );
};

export const Pendant = ({ x, y }) => {
    const top = P(x, y, 150);
    const bot = P(x, y, 110);
    const s1 = P(x - 10, y, 110);
    const s2 = P(x + 10, y, 110);
    const s3 = P(x + 4, y, 97);
    const s4 = P(x - 4, y, 97);
    const [gx, gy] = P(x, y, 82);
    return (
        <g>
            <line x1={top[0]} y1={top[1]} x2={bot[0]} y2={bot[1]} stroke="#9AA3B8" strokeWidth={1.4} />
            <polygon points={pts(s1, s2, s3, s4)} fill="#1E3A5C" opacity={0.88} />
            <ellipse cx={gx} cy={gy} rx={22} ry={9} fill="url(#wsGlow)" opacity={0.55} />
        </g>
    );
};

export const GlassPanel = ({ x1, y1, x2, y2, zBase = 20, zTop = 130 }) => {
    const b1 = P(x1, y1, zBase);
    const b2 = P(x2, y2, zBase);
    const t1 = P(x1, y1, zTop);
    const t2 = P(x2, y2, zTop);
    return (
        <g>
            <polygon points={pts(b1, b2, t2, t1)} fill="url(#wsGlass)" stroke="rgba(110,132,182,0.5)" strokeWidth={1.3} />
            <line x1={t1[0]} y1={t1[1]} x2={t2[0]} y2={t2[1]} stroke="rgba(255,255,255,0.85)" strokeWidth={1.4} />
        </g>
    );
};
